import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import Layout from '@/shared/components/Layout'
import AdminLayout from '@/shared/components/AdminLayout'
import RequirePermission from '@/shared/components/RequirePermission'
import ErrorBoundary from '@/shared/components/ErrorBoundary'
import LoadingFallback from '@/shared/components/LoadingFallback'
import { Permission } from '@/shared/constants/permissions'
import HomePage from '@/features/blog/pages/HomePage'
import BlogListPage from '@/features/blog/pages/BlogListPage'
import PostPage from '@/features/blog/pages/PostPage'
import TagPage from '@/features/blog/pages/TagPage'
import CategoryPage from '@/features/blog/pages/CategoryPage'
import SeriesPage from '@/features/blog/pages/SeriesPage'
import LoginPage from '@/features/auth/pages/LoginPage'
import RegisterPage from '@/features/auth/pages/RegisterPage'
import VerifyPage from '@/features/auth/pages/VerifyPage'
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage'
import ResetPasswordPage from '@/features/auth/pages/ResetPasswordPage'
import PageTracker from '@/features/analytics/components/PageTracker'
import TermsPage from '@/shared/pages/TermsPage'
import PrivacyPage from '@/shared/pages/PrivacyPage'
import NotFoundPage from '@/shared/pages/NotFoundPage'

// Lazy-loaded pages (code splitting)
const CVPage = lazy(() => import('@/features/cv/pages/CVPage'))
const SettingsPage = lazy(() => import('@/features/auth/pages/SettingsPage'))
const AdminLoginPage = lazy(() => import('@/features/admin/pages/AdminLoginPage'))
const AdminDashboard = lazy(() => import('@/features/admin/pages/AdminDashboard'))
const NewPostPage = lazy(() => import('@/features/admin/pages/NewPostPage'))
const EditPostPage = lazy(() => import('@/features/admin/pages/EditPostPage'))
const AdminComments = lazy(() => import('@/features/admin/pages/AdminComments'))
const AdminTags = lazy(() => import('@/features/admin/pages/AdminTags'))
const AdminCategories = lazy(() => import('@/features/admin/pages/AdminCategories'))
const AdminCV = lazy(() => import('@/features/admin/pages/AdminCV'))
const AdminNews = lazy(() => import('@/features/news/pages/AdminNews'))
const AdminUsers = lazy(() => import('@/features/admin/pages/AdminUsers'))
const AdminAnalytics = lazy(() => import('@/features/analytics/pages/AdminAnalytics'))
const AdminSystem = lazy(() => import('@/features/admin/pages/AdminSystem'))
const AdminMedia = lazy(() => import('@/features/admin/pages/AdminMedia'))
const AdminFiles = lazy(() => import('@/features/admin/pages/AdminFiles'))
const AdminLogs = lazy(() => import('@/features/admin/pages/AdminLogs'))
const AdminSeries = lazy(() => import('@/features/admin/pages/AdminSeries'))

// AI Content — layout shell + sub-pages
const AdminAIContent = lazy(() => import('@/features/ai-content/pages/AdminAIContent'))
const AIOverviewPage = lazy(() => import('@/features/ai-content/pages/AIOverviewPage'))
const AISourcesPage = lazy(() => import('@/features/ai-content/pages/AISourcesPage'))
const AISourceDetailPage = lazy(() => import('@/features/ai-content/pages/AISourceDetailPage'))
const AIArticlesPage = lazy(() => import('@/features/ai-content/pages/AIArticlesPage'))
const AIArticleDetail = lazy(() => import('@/features/ai-content/pages/AIArticleDetail'))
const AIArticlesKanban = lazy(() => import('@/features/ai-content/pages/AIArticlesKanban'))
const AIAgentsPage = lazy(() => import('@/features/ai-content/pages/AIAgentsPage'))
const AIAgentDetailPage = lazy(() => import('@/features/ai-content/pages/AIAgentDetailPage'))
const AIPipelinesPage = lazy(() => import('@/features/ai-content/pages/AIPipelinesPage'))
const AIPipelineDetailPage = lazy(() => import('@/features/ai-content/pages/AIPipelineDetailPage'))
const AIPromptsPage = lazy(() => import('@/features/ai-content/pages/AIPromptsPage'))
const AIStylesPage = lazy(() => import('@/features/ai-content/pages/AIStylesPage'))
const AIJobsPage = lazy(() => import('@/features/ai-content/pages/AIJobsPage'))
const AITokenUsagePage = lazy(() => import('@/features/ai-content/pages/AITokenUsagePage'))
const AIToolsPage = lazy(() => import('@/features/ai-content/pages/AIToolsPage'))
const AITrendingTopicsPage = lazy(() => import('@/features/ai-content/pages/AITrendingTopicsPage'))
const AIOPMLImportPage = lazy(() => import('@/features/ai-content/pages/AIOPMLImportPage'))
const AIConfigPage = lazy(() => import('@/features/ai-content/pages/AIConfigPage'))
const AIReaderFeedbackPage = lazy(() => import('@/features/ai-content/pages/AIReaderFeedbackPage'))
const AIExperimentsPage = lazy(() => import('@/features/ai-content/pages/AIExperimentsPage'))
const AIAddSourceWizard = lazy(() => import('@/features/ai-content/pages/AIAddSourceWizard'))
const ScraperIDEPage = lazy(() => import('@/features/ai-content/pages/ScraperIDEPage'))
const AIOperationsPage = lazy(() => import('@/features/ai-content/pages/AIOperationsPage'))
const AIRunnersPage = lazy(() => import('@/features/ai-content/pages/AIRunnersPage'))

// Email
const EmailComposePage = lazy(() => import('@/features/email/pages/EmailComposePage'))
const EmailTemplatesPage = lazy(() => import('@/features/email/pages/EmailTemplatesPage'))
const EmailTemplateEditPage = lazy(() => import('@/features/email/pages/EmailTemplateEditPage'))

// Advanced features
const AISettingsPage = lazy(() => import('@/features/ai-content/pages/AISettingsPage'))
const AIComponentLibraryPage = lazy(() => import('@/features/ai-content/pages/AIComponentLibraryPage'))
const AIIssuesPage = lazy(() => import('@/features/ai-content/pages/AIIssuesPage'))
const AIProfilesPage = lazy(() => import('@/features/ai-content/pages/AIProfilesPage'))
const AIFlowBuilderPage = lazy(() => import('@/features/ai-content/pages/AIFlowBuilderPage'))

// Gmail
const GmailPage = lazy(() => import('@/features/gmail/pages/GmailPage'))

// OCI Repeater
const AdminOciRepeater = lazy(() => import('@/features/oci-repeater/pages/AdminOciRepeater'))

// Claudex sharing
const AdminClaudexSharing = lazy(() => import('@/features/claudex-sharing/pages/AdminClaudexSharing'))


export default function App() {
  return (
    <ErrorBoundary>
    <PageTracker />
    <Suspense fallback={<LoadingFallback />}>
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="blog" element={<BlogListPage />} />
        <Route path="blog/:id/:slug?" element={<PostPage />} />
        <Route path="tag/:id/:slug?" element={<TagPage />} />
        <Route path="category/:id/:slug?" element={<CategoryPage />} />
        <Route path="series/:id/:slug?" element={<SeriesPage />} />
        <Route path="cv" element={<CVPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Scraper IDE — full-screen, outside AdminLayout */}
      <Route path="/admin/ai/scraper-ide/:sourceId" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><ScraperIDEPage /></RequirePermission>} />
      {/* Flow Builder — full-screen, outside AdminLayout */}
      <Route path="/admin/ai/flow-builder/:flowId" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIFlowBuilderPage /></RequirePermission>} />

      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="posts" element={<RequirePermission permission={Permission.POSTS_READ}><AdminDashboard /></RequirePermission>} />
        <Route path="posts/new" element={<RequirePermission permission={Permission.POSTS_WRITE}><NewPostPage /></RequirePermission>} />
        <Route path="posts/:id" element={<RequirePermission permission={Permission.POSTS_WRITE}><EditPostPage /></RequirePermission>} />
        <Route path="comments" element={<RequirePermission permission={Permission.COMMENTS_MODERATE}><AdminComments /></RequirePermission>} />
        <Route path="tags" element={<RequirePermission permission={Permission.TAGS_MANAGE}><AdminTags /></RequirePermission>} />
        <Route path="categories" element={<RequirePermission permission={Permission.CATEGORIES_MANAGE}><AdminCategories /></RequirePermission>} />
        <Route path="series" element={<RequirePermission permission={Permission.TAGS_MANAGE}><AdminSeries /></RequirePermission>} />
        <Route path="cv" element={<RequirePermission permission={Permission.CV_MANAGE}><AdminCV /></RequirePermission>} />
        <Route path="news" element={<RequirePermission permission={Permission.NEWS_MANAGE}><AdminNews /></RequirePermission>} />
        <Route path="users" element={<RequirePermission permission={Permission.USERS_MANAGE}><AdminUsers /></RequirePermission>} />
        <Route path="analytics" element={<RequirePermission permission={Permission.ANALYTICS_VIEW}><AdminAnalytics /></RequirePermission>} />
        <Route path="system" element={<RequirePermission permission={Permission.SYSTEM_VIEW}><AdminSystem /></RequirePermission>} />
        <Route path="media" element={<RequirePermission permission={Permission.UPLOADS_WRITE}><AdminMedia /></RequirePermission>} />
        <Route path="files" element={<RequirePermission permission={Permission.UPLOADS_WRITE}><AdminFiles /></RequirePermission>} />
        <Route path="logs" element={<RequirePermission permission={Permission.SYSTEM_VIEW}><AdminLogs /></RequirePermission>} />
        <Route path="oci-repeater" element={<RequirePermission permission={Permission.OCI_REPEATER_MANAGE}><AdminOciRepeater /></RequirePermission>} />
        <Route path="claudex" element={<RequirePermission permission={Permission.CLAUDEX_SHARING}><AdminClaudexSharing /></RequirePermission>} />

        {/* Email */}
        <Route path="email/compose" element={<RequirePermission permission={Permission.EMAIL_SEND}><EmailComposePage /></RequirePermission>} />
        <Route path="email/templates" element={<RequirePermission permission={Permission.EMAIL_SEND}><EmailTemplatesPage /></RequirePermission>} />
        <Route path="email/templates/new" element={<RequirePermission permission={Permission.EMAIL_TEMPLATES_WRITE}><EmailTemplateEditPage /></RequirePermission>} />
        <Route path="email/templates/:id" element={<RequirePermission permission={Permission.EMAIL_TEMPLATES_WRITE}><EmailTemplateEditPage /></RequirePermission>} />

        {/* Gmail */}
        <Route path="gmail" element={<RequirePermission permission={Permission.EMAIL_SEND}><GmailPage /></RequirePermission>} />

        {/* AI Content — nested layout with sub-routes */}
        <Route path="ai" element={<RequirePermission permission={Permission.AI_CONTENT_VIEW}><AdminAIContent /></RequirePermission>}>
          <Route index element={<AIOverviewPage />} />
          <Route path="sources" element={<AISourcesPage />} />
          <Route path="sources/new" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIAddSourceWizard /></RequirePermission>} />
          <Route path="sources/:id" element={<AISourceDetailPage />} />
          <Route path="articles" element={<AIArticlesPage />} />
          <Route path="articles/:id" element={<AIArticleDetail />} />
          <Route path="kanban" element={<AIArticlesKanban />} />
          <Route path="agents" element={<AIAgentsPage />} />
          <Route path="agents/:id" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIAgentDetailPage /></RequirePermission>} />
          <Route path="pipelines" element={<AIPipelinesPage />} />
          <Route path="pipelines/new" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIPipelineDetailPage /></RequirePermission>} />
          <Route path="pipelines/:id" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIPipelineDetailPage /></RequirePermission>} />
          <Route path="prompts" element={<AIPromptsPage />} />
          <Route path="styles" element={<AIStylesPage />} />
          <Route path="jobs" element={<AIJobsPage />} />
          <Route path="usage" element={<AITokenUsagePage />} />
          <Route path="tools" element={<AIToolsPage />} />
          <Route path="trending" element={<AITrendingTopicsPage />} />
          <Route path="import-opml" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIOPMLImportPage /></RequirePermission>} />
          <Route path="config" element={<AIConfigPage />} />
          <Route path="feedback" element={<AIReaderFeedbackPage />} />
          <Route path="experiments" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIExperimentsPage /></RequirePermission>} />
          <Route path="settings" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AISettingsPage /></RequirePermission>} />
          <Route path="components" element={<AIComponentLibraryPage />} />
          <Route path="issues" element={<AIIssuesPage />} />
          <Route path="profiles" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIProfilesPage /></RequirePermission>} />
          <Route path="operations" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIOperationsPage /></RequirePermission>} />
          <Route path="runners" element={<RequirePermission permission={Permission.AI_CONTENT_MANAGE}><AIRunnersPage /></RequirePermission>} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
    </ErrorBoundary>
  )
}
